import * as React from 'react';
import { useState, useEffect } from 'react';
import { useObjectVal } from 'react-firebase-hooks/database';
import { Row, Col, Button, Collapse, Nav, NavItem, NavLink } from 'reactstrap';
import { AccountsTable } from '../AccountsTable';
import { ErrorIcon, TickIcon } from '../../../components/Icons';
import { SelectColumnFilter } from '../../../components/table/SelectColumnfilter';
import { Redirect } from 'react-router-dom';
import { Can } from '../../../auth/Can';
import { ExportToExcel } from '../ExportToExcel';
import { DefaultColumnFilter } from '../../../components/table/DefaultFilter';
import { useTable, useSortBy, useFilters, usePagination } from 'react-table';
import { Loader } from '../../../components/Loader';
import { fuzzyTextFilterFnExp } from '../AccountsContainer';
import { logger } from '../../../logging';
import { useAuthState } from 'react-firebase-hooks/auth';
import moment from 'moment';
import { ColumnsSelector } from '../../../components/table/ColumnsSelector';
import * as firebaseService from '../../../services/firebase';
import * as R from 'ramda';
import { Countries } from '../../country-manager/countriesModel';
import { getAuth } from 'firebase/auth';
import { ref } from '../../../utils/firebase';
import { useFrozenAccountsTablePrefs } from '../useFrozenAccountsTablePrefs';
import { useAccountsTablePrefsSync } from '../useAccountsTablePrefsSync';
import { AccountsResetFiltersButton } from '../AccountsResetFiltersButton';

const pagesStyles = require('../../pages.css');
const accountsStyles = require('../accounts.css');

import classnames from 'classnames';

const TABS = {
  ACTIVE: 0,
  INACTIVE: 1,
  DECOMM_IN_PROGRESS: 2,
  DECOMM_COMPLETE: 3,
};
export interface CommunityAccount {
  account_name: string;
  account_id: string;
  country_code: string;
  active: boolean;
  created_by: string;
  created_at: number;
  updated_by?: string;
  updated_at?: number;
  token_error?: string | boolean;
}

interface Accounts {
  accounts: CommunityAccount[];
}

const getEnumLabels = (values, countriesModel: Countries) => {
  const enumMappings = {
    country_code: 'operatingCountries',
  };

  const enumsToLookup = ['country_code'];

  const labels = { ...values };
  try {
    enumsToLookup.forEach((type) => {
      if (type === 'country_code') {
        labels[type] = countriesModel.getName(values[type]);
      }
    });
  } catch (e) {
    logger.error(e, ' account_name: ', values.account_name, values);
  }

  return labels;
};

export const Accounts = ({
  showActive,
  setShowActive,
  countriesModel,
  onResetAccountsFilters,
}) => {
  const TABLE_NAME = 'communityAccounts';
  const damApp = firebaseService.getDAMApp();
  const damAuth = getAuth(damApp);
  // get accounts from firebase hooks here
  const accountsRef = ref('accounts/community');
  const credentialsRef = ref('credentials');

  const [createAccount, setCreateAccount] = useState(false);
  //const [showInactiveAccounts, setShowInactiveAccounts] = useState(false);

  const [accountsList, loading, error] = useObjectVal(accountsRef as any);
  const [allCredentials, credsLoading, credsError] = useObjectVal(
    credentialsRef as any
  );
  const [values, setValues] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [user] = useAuthState(damAuth as any);

  const [isColumnsSelectOpen, setIsColumnsSelectOpen] = useState(false);
  const uid = damAuth.currentUser.uid;
  const userColsRef = ref(`user_preferences/${uid}/columns/${TABLE_NAME}`);
  const [userCols, userColsLoading, userColsError] = useObjectVal(
    userColsRef as any
  );

  const defaultTablePrefs = React.useMemo(
    () => ({
      sortBy: [{ id: 'created_at', desc: true }] as Array<{
        id: string;
        desc: boolean;
      }>,
      pageSize: 20,
      pageIndex: 0,
    }),
    []
  );

  const { frozenPrefs, prefsReady } = useFrozenAccountsTablePrefs(
    uid,
    TABLE_NAME,
    userColsLoading,
    defaultTablePrefs
  );

  // when accountsList changes
  // and there are no values yet
  // put values into the correct shape + update state with mapped values
  useEffect(() => {
    if (accountsList && countriesModel) {
      const accounts = Object.keys(accountsList).filter((k) => {
        switch (activeTab) {
          case TABS.ACTIVE:
            return accountsList[k].active === true;
          case TABS.INACTIVE:
            return (
              accountsList[k].active === false &&
              !accountsList[k].decommissioning_status?.status
            );
          case TABS.DECOMM_IN_PROGRESS:
            return (
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
                'pending' ||
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
                'failed'
            );
          case TABS.DECOMM_COMPLETE:
            return (
              accountsList[k].decommissioning_status?.status?.toLowerCase() ===
              'complete'
            );
        }
      });

      setValues(
        accounts.map((k) => {
          const value = accountsList[k];
          const labels = getEnumLabels(value, countriesModel);

          let token_status = 'Loading...';
          let expiring_tokens = 'Loading...';
          let expired_count = 0;
          let expiring_count = 0;

          if (allCredentials) {
            const credentialValues = (allCredentials as any)[k] || {};
            const activeCreds = Object.values(credentialValues).filter(
              (v: any) => v.active === true || v.active === 'true'
            );

            if (activeCreds.length === 0) {
              token_status = 'No active credentials';
            } else if (
              value.token_error === false ||
              value.token_error === 'false'
            ) {
              token_status = 'Valid';
            } else if (typeof value.token_error === 'string') {
              token_status = value.token_error;
            } else {
              token_status = 'Pending Verification';
            }

            // Calculate expiring tokens
            const date = moment();
            activeCreds.forEach((val: any) => {
              const expiryDiff =
                365 - date.diff(moment.unix(val.apiTokenCreatedDate), 'days');

              if (expiryDiff <= 7) {
                if (expiryDiff <= 0) {
                  expired_count++;
                } else {
                  expiring_count++;
                }
              }
            });

            if (expired_count || expiring_count) {
              const parts = [];
              if (expired_count) {
                parts.push(`${expired_count} expired token`);
              }
              if (expiring_count) {
                parts.push(`${expiring_count} token about to expire`);
              }
              expiring_tokens = parts.join(' ');
            } else {
              expiring_tokens = 'No expiring tokens';
            }
          } else if (credsError) {
            token_status = 'Error loading credentials';
            expiring_tokens = 'Error loading credentials';
          }

          return {
            ...labels,
            key: k,
            token_status,
            expiring_tokens,
            expired_count,
            expiring_count,
          };
        })
      );
    }
  }, [
    accountsList,
    allCredentials,
    credsError,
    activeTab,
    showActive,
    countriesModel,
  ]); // when accountsList, allCredentials, or countriesModel changes

  const newAccount = () => {
    setCreateAccount(true);
  };

  const updateTableData = (rowIndex, columnId, value) => {
    setValues((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const TokenExpiryList = ({ value, row: { original } }) => {
    if (value === 'Loading...') {
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', color: '#ffc107' }}
        >
          <span>Loading...</span>
        </div>
      );
    }
    const expiredCount = original.expired_count || 0;
    const expiringCount = original.expiring_count || 0;

    return expiredCount || expiringCount ? (
      <p>
        {expiredCount ? <span>{expiredCount} expired token</span> : null}
        <br />
        {expiringCount ? (
          <span>{expiringCount} token about to expire</span>
        ) : null}
      </p>
    ) : (
      <p>No expiring tokens</p>
    );
  };

  const TokenStatusCell = ({ value }) => {
    if (value === 'Loading...') {
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', color: '#ffc107' }}
        >
          <span>Loading...</span>
        </div>
      );
    }

    if (value === 'No active credentials') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', color: 'gray' }}>
          <span>No active credentials</span>
        </div>
      );
    }

    if (value === 'Valid') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', color: 'green' }}>
          <TickIcon fill="green" />
          <span style={{ marginLeft: '5px' }}>Valid</span>
        </div>
      );
    } else if (value === 'Pending Verification') {
      return (
        <div
          style={{ display: 'flex', alignItems: 'center', color: '#ffc107' }}
        >
          <span>Pending Verification</span>
        </div>
      );
    } else {
      return (
        <div
          className={accountsStyles.addressbookSyncErrorCell}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <ErrorIcon errors={[value]} fill="red" />
          <span style={{ marginLeft: '5px' }}>{value}</span>
        </div>
      );
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'DAM key',
        accessor: 'key',
        id: 'key',
      },
      {
        Header: 'Account Name',
        accessor: 'account_name',
        id: 'account_name',
      },
      {
        Header: 'Account ID',
        accessor: 'account_id',
        id: 'account_id',
      },
      {
        Header: 'Territory (Operating Country)',
        accessor: 'country_code',
        Filter: SelectColumnFilter,
        filter: 'equals',
        id: 'country_code',
      },
      {
        Header: 'Created',
        accessor: (row) =>
          moment.unix(+row.created_at).format('YYYY-MM-DD HH:mm:ss'),
        id: 'created_at',
      },

      {
        Header: 'Modified',
        accessor: (row) =>
          moment
            .unix(+row.modified_at || +row.created_at)
            .format('YYYY-MM-DD HH:mm:ss'),
        id: 'modified_at',
      },
      {
        Header: 'Token Status',
        accessor: 'token_status',
        Cell: TokenStatusCell,
        filter: (rows, id, filterValue) => {
          return rows.filter((row) => {
            const rowValue = row.values[id];
            if (!rowValue) return false;
            return String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase());
          });
        },
        id: 'token_status',
      },
      {
        Header: 'Expiring tokens',
        accessor: 'expiring_tokens',
        Cell: TokenExpiryList,
        id: 'expiring_tokens',
      },
    ],
    []
  );

  const filterTypes = React.useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFnExp,
    }),
    []
  );

  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  // memoize values - the heavy lifting is now done in the above useEffect
  const records = React.useMemo(() => values, [values]);

  let hiddenColumns: any = R.keys(
    R.pickBy((val, key) => val === false, userCols)
  );

  const tableData = useTable(
    {
      columns,
      data: records,
      filterTypes,
      defaultColumn,
      autoResetFilters: false,
      autoResetPage: false,
      autoResetSortBy: false,
      initialState: {
        sortBy: [
          {
            id: 'created_at',
            desc: true,
          },
        ],
        pageSize: 20,
        pageIndex: 0,
      },
      autoResetHiddenColumns: false,
      updateTableData,
    } as any,
    useFilters,
    useSortBy,
    usePagination
  ) as any;

  // Hydrate from RTDB then debounce-persist table + tab state.
  useAccountsTablePrefsSync({
    uid,
    tableName: TABLE_NAME,
    prefsReady,
    frozenPrefs,
    tableData,
    activeTab,
    setActiveTab,
  });

  React.useEffect(() => {
    hiddenColumns = R.keys(R.pickBy((val, key) => val === false, userCols));
    tableData.setHiddenColumns(hiddenColumns);
  }, [userCols]);

  if (createAccount === true) {
    return (
      <React.Fragment>
        <Redirect to="/accounts/new/community" />
      </React.Fragment>
    );
  }

  if (loading || userColsLoading || credsLoading || !prefsReady) {
    return <Loader loading={true} />;
  }

  if (error || credsError) {
    return <div>{`Error: ${error?.message || credsError?.message}`}</div>;
  }

  if (userColsError) {
    return <div>Error loading cols</div>;
  }

  // depends on community account name being unique
  const accountsFiltered = () => {
    const rows = tableData.rows.map((r) => r.values);

    return records.filter((v: any) => {
      const found = rows.find((r) => {
        return r.account_name === v.account_name;
      });

      if (found) {
        return true;
      }

      return false;
    });
  };

  const toggleColumnsSelect = () =>
    setIsColumnsSelectOpen(!isColumnsSelectOpen);

  return (
    <div className={pagesStyles.fullHeight}>
      {values && (
        <div className={`${pagesStyles.noPadding} ${pagesStyles.fullHeight}`}>
          <Row>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === TABS.ACTIVE })}
                  onClick={() => setActiveTab(TABS.ACTIVE)}
                >
                  Active Accounts
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.INACTIVE,
                  })}
                  onClick={() => setActiveTab(TABS.INACTIVE)}
                >
                  Inactive Accounts
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.DECOMM_IN_PROGRESS,
                  })}
                  onClick={() => setActiveTab(TABS.DECOMM_IN_PROGRESS)}
                >
                  Decommission In Progress
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === TABS.DECOMM_COMPLETE,
                  })}
                  onClick={() => setActiveTab(TABS.DECOMM_COMPLETE)}
                >
                  Decommissioned
                </NavLink>
              </NavItem>
            </Nav>
          </Row>
          <Row className={pagesStyles.headerRow}>
            <Col sm={{ size: 12 }} className={pagesStyles.noPadding}>
              <Can I="create" an="account">
                <Button
                  onClick={(e) => newAccount()}
                  className={accountsStyles.button}
                >
                  Create New Account
                </Button>
              </Can>
              <Can I="export" an="accounts">
                <ExportToExcel
                  csvData={accountsFiltered()}
                  fileName={'accounts-community'}
                  user={user}
                />
              </Can>
              <Button
                onClick={toggleColumnsSelect}
                className={accountsStyles.button}
              >
                Columns
              </Button>
              <AccountsResetFiltersButton
                onResetAccountsFilters={onResetAccountsFilters}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Collapse isOpen={isColumnsSelectOpen}>
                <ColumnsSelector
                  columns={columns}
                  userCols={userCols}
                  tableName={TABLE_NAME}
                />
              </Collapse>
            </Col>
          </Row>
          <div className={pagesStyles.fullHeight}>
            <Row className={pagesStyles.fullHeight}>
              <Col sm={{ size: 12 }} className={pagesStyles.fullHeight}>
                <AccountsTable {...tableData} system="community" />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};
